import React, {useState} from 'react';

import {AnalyticsModalType} from 'analytics/AnalyticsTypes';
import {getRestrictedSampleUsersDisplayText} from 'inventory/sample-status/RestrictedSampleUsersTextUtil';
import {SampleControlsFeatureLevel} from 'inventory/sample-status/SampleControlsFeatureLevel';
import {hasMinimumSCRFeatureLevel} from 'inventory/sample-status/SampleControlsFeatureLevelUtil';
import {ContainerPropertiesUpdate} from 'samples/ContainerDAO';
import {bulkUpdateContainerPropertiesWithDataMods} from 'samples/StorableActions';

import BulkUpdateSampleRolesModal, {
  BaseBulkUpdateSampleRolesModalProps,
  BulkUpdateSampleRolesAction,
  computeNewPartyIds,
  ItemWithSampleRoles,
} from './BulkUpdateSampleRolesModal';

interface BulkUpdateSampleOwnersModalProps extends BaseBulkUpdateSampleRolesModalProps {}

export default function BulkUpdateSampleOwnersModal(props: BulkUpdateSampleOwnersModalProps): JSX.Element {
  const [notice, setNotice] = useState<string | null>(null);

  const analyticsMetadata = {
    ...props.analyticsMetadata,
    modalType: AnalyticsModalType.BULK_UPDATE_SAMPLE_OWNERS,
  };

  const handleChangeAction = (newAction: BulkUpdateSampleRolesAction): void => {
    if (
      !hasMinimumSCRFeatureLevel(SampleControlsFeatureLevel.V2) &&
      newAction === BulkUpdateSampleRolesAction.CLEAR
    ) {
      setNotice(
        'By removing all sample owners for these items, you will no longer require authorization for ' +
          `them and will remove all ${getRestrictedSampleUsersDisplayText()} as well.`
      );
    } else if (
      !hasMinimumSCRFeatureLevel(SampleControlsFeatureLevel.V2) &&
      newAction === BulkUpdateSampleRolesAction.REMOVE
    ) {
      setNotice(
        'If you remove all sample owners for an item, you will no longer require authorization for ' +
          `it and will remove all ${getRestrictedSampleUsersDisplayText()} as well.`
      );
    } else {
      setNotice(null);
    }
  };

  const handleRequestSubmit = (
    selectedItems: Array<ItemWithSampleRoles>,
    action: BulkUpdateSampleRolesAction,
    partyIdsByItemId: {[itemId: string]: Set<string>}
  ): void => {
    const containerUpdates: Array<ContainerPropertiesUpdate> =
      action === BulkUpdateSampleRolesAction.CLEAR
        ? selectedItems.map((item) => ({
            id: item.id,
            sampleRoles: {
              sampleOwnerIds: [],
              ...(!hasMinimumSCRFeatureLevel(SampleControlsFeatureLevel.V2) && {restrictedSampleUserIds: []}),
            },
          }))
        : action === BulkUpdateSampleRolesAction.REMOVE
          ? selectedItems.map((item): ContainerPropertiesUpdate => {
              const newSampleOwnerIds = computeNewPartyIds(
                action,
                item.sampleRoles.sampleOwners.map(({id}) => id),
                partyIdsByItemId[item.id]
              );
              return {
                id: item.id,
                sampleRoles: {
                  sampleOwnerIds: newSampleOwnerIds,
                  ...(!hasMinimumSCRFeatureLevel(SampleControlsFeatureLevel.V2) &&
                    newSampleOwnerIds.length === 0 && {restrictedSampleUserIds: []}),
                },
              };
            })
          : selectedItems.map((item) => ({
              id: item.id,
              sampleRoles: {
                sampleOwnerIds: computeNewPartyIds(
                  action,
                  item.sampleRoles.sampleOwners.map(({id}) => id),
                  partyIdsByItemId[item.id]
                ),
              },
            }));
    bulkUpdateContainerPropertiesWithDataMods(containerUpdates, analyticsMetadata);
    props.onRequestClose();
  };

  return (
    <BulkUpdateSampleRolesModal
      {...props}
      label="sample owners"
      getNoticeFn={() => ({header: notice})}
      onRequestSubmit={handleRequestSubmit}
      onChangeAction={handleChangeAction}
      getItemValueFn={(item) => item.sampleRoles.sampleOwners}
      onRequestClose={props.onRequestClose}
      analyticsMetadata={analyticsMetadata}
      allowBenchlingApps={false}
    />
  );
}
